import { 
  Campaign, 
  Project,
  ManipulatedVariable, 
  StepTest, 
  Disturbance, 
  MVStatus 
} from '../types';
import { 
  INITIAL_CAMPAIGNS, 
  INITIAL_MVS, 
  INITIAL_STEP_TESTS, 
  INITIAL_DISTURBANCES 
} from '../data/kfsSeedData';
import { calculateReadinessScore } from './readinessCalculator';

const STORAGE_KEYS = {
  CAMPAIGNS: 'apc_campaigns_v1',
  MVS: 'apc_mvs_v1',
  STEP_TESTS: 'apc_step_tests_v1',
  DISTURBANCES: 'apc_disturbances_v1',
  CURRENT_CAMPAIGN: 'apc_current_campaign_id_v1',
  CURRENT_ROLE: 'apc_current_role_v1',
  INITIALIZED: 'apc_storage_initialized_v2',
};

// Check if we are running with active Cloudflare Pages Functions
let useRemoteApi = false;

async function checkApiAvailability(): Promise<boolean> {
  try {
    // Ping /api/init to automatically ensure Cloudflare D1 tables and 36 MVs are created
    const initRes = await fetch('/api/init');
    if (initRes.ok) {
      useRemoteApi = true;
      return true;
    }
    const res = await fetch('/api/campaigns', { method: 'GET' });
    if (res.ok && res.headers.get('content-type')?.includes('application/json')) {
      useRemoteApi = true;
      return true;
    }
  } catch (e) {
    // API not mounted, fallback to local storage
  }
  useRemoteApi = false;
  return false;
}

// Initializer to ensure local storage has seed data
export function initializeLocalStorage(): void {
  if (typeof window === 'undefined') return;

  // Once initialized, do not re-seed deleted campaigns or MVs
  if (localStorage.getItem(STORAGE_KEYS.INITIALIZED)) {
    return;
  }
  localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');

  if (!localStorage.getItem(STORAGE_KEYS.CAMPAIGNS)) {
    localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(INITIAL_CAMPAIGNS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.MVS)) {
    localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(INITIAL_MVS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.STEP_TESTS)) {
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(INITIAL_STEP_TESTS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.DISTURBANCES)) {
    localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(INITIAL_DISTURBANCES));
  }
}

export const StorageService = {
  async init(): Promise<void> {
    initializeLocalStorage();
    await checkApiAvailability();
  },

  getCurrentCampaignId(): string {
    return localStorage.getItem(STORAGE_KEYS.CURRENT_CAMPAIGN) || '';
  },

  setCurrentCampaignId(id: string): void {
    localStorage.setItem(STORAGE_KEYS.CURRENT_CAMPAIGN, id);
  },

  getCurrentProjectId(): string {
    return this.getCurrentCampaignId();
  },

  setCurrentProjectId(id: string): void {
    this.setCurrentCampaignId(id);
  },

  // ===== PROJECTS / CAMPAIGNS =====
  async getCampaigns(): Promise<Campaign[]> {
    if (useRemoteApi) {
      try {
        const res = await fetch('/api/campaigns');
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error, using local fallback:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.CAMPAIGNS);
    return raw ? JSON.parse(raw) : [];
  },

  async getProjects(): Promise<Project[]> {
    return this.getCampaigns();
  },

  async createCampaign(campaign: Campaign): Promise<Campaign> {
    if (useRemoteApi) {
      try {
        const res = await fetch('/api/campaigns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(campaign),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error creating campaign:', err);
      }
    }
    const campaigns = await this.getCampaigns();
    const updated = [campaign, ...campaigns];
    localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(updated));
    return campaign;
  },

  async createProject(project: Project): Promise<Project> {
    return this.createCampaign(project);
  },

  async updateCampaign(campaign: Campaign): Promise<Campaign> {
    if (useRemoteApi) {
      try {
        const res = await fetch('/api/campaigns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(campaign),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error updating campaign:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.CAMPAIGNS);
    const campaigns: Campaign[] = raw ? JSON.parse(raw) : [];
    const idx = campaigns.findIndex((c) => c.id === campaign.id);
    if (idx !== -1) {
      campaigns[idx] = { ...campaigns[idx], ...campaign };
    } else {
      campaigns.push(campaign);
    }
    localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(campaigns));
    return campaign;
  },

  async updateProject(project: Project): Promise<Project> {
    return this.updateCampaign(project);
  },

  async deleteProject(id: string): Promise<boolean> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/campaigns?id=${encodeURIComponent(id)}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Server responded with status ${res.status}`);
        }
      } catch (err: any) {
        console.error('API error deleting project:', err);
        throw err;
      }
    }

    // Cascade delete in localStorage
    const rawCampaigns = localStorage.getItem(STORAGE_KEYS.CAMPAIGNS);
    if (rawCampaigns) {
      const list: Campaign[] = JSON.parse(rawCampaigns);
      const filtered = list.filter((c) => c.id !== id);
      localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(filtered));
    }

    const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
    if (rawMvs) {
      const list: ManipulatedVariable[] = JSON.parse(rawMvs);
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(list.filter((m) => m.campaignId !== id)));
    }

    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    if (rawSteps) {
      const list: StepTest[] = JSON.parse(rawSteps);
      localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(list.filter((s) => s.campaignId !== id)));
    }

    const rawDist = localStorage.getItem(STORAGE_KEYS.DISTURBANCES);
    if (rawDist) {
      const list: Disturbance[] = JSON.parse(rawDist);
      localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(list.filter((d) => d.campaignId !== id)));
    }

    // If active project was deleted, switch to next available or clear
    if (this.getCurrentProjectId() === id) {
      const remaining = await this.getProjects();
      if (remaining.length > 0) {
        this.setCurrentProjectId(remaining[0].id);
      } else {
        this.setCurrentProjectId('');
      }
    }

    return true;
  },

  async deleteCampaign(id: string): Promise<boolean> {
    return this.deleteProject(id);
  },

  // ===== MVS =====
  async getMVs(campaignId?: string): Promise<ManipulatedVariable[]> {
    const targetCampaign = campaignId || this.getCurrentCampaignId();
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/mvs?campaignId=${encodeURIComponent(targetCampaign)}`);
        if (res.ok) {
          const remoteMvs: ManipulatedVariable[] = await res.json();
          const raw = localStorage.getItem(STORAGE_KEYS.MVS);
          let allMvs: ManipulatedVariable[] = raw ? JSON.parse(raw) : [];
          allMvs = allMvs.filter((m) => m.campaignId !== targetCampaign).concat(remoteMvs);
          localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));
          return remoteMvs;
        }
      } catch (err) {
        console.warn('API error, using local fallback for MVs:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.MVS);
    const allMvs: ManipulatedVariable[] = raw ? JSON.parse(raw) : [];
    return allMvs.filter((m) => m.campaignId === targetCampaign);
  },

  async updateMV(mv: ManipulatedVariable, oldTag?: string): Promise<ManipulatedVariable> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/mvs`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...mv, oldTag }),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error updating MV:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.MVS);
    const allMvs: ManipulatedVariable[] = raw ? JSON.parse(raw) : INITIAL_MVS;
    const idx = allMvs.findIndex((m) => m.id === mv.id);
    if (idx !== -1) {
      allMvs[idx] = { ...mv, updatedAt: new Date().toISOString() };
    } else {
      allMvs.push({ ...mv, updatedAt: new Date().toISOString() });
    }
    localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));

    // If tag was changed, update references in localStorage step tests
    if (oldTag && oldTag !== mv.tag) {
      const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
      if (rawSteps) {
        const steps: StepTest[] = JSON.parse(rawSteps);
        let changed = false;
        const updatedSteps = steps.map((s) => {
          if (s.mvTag?.toUpperCase() === oldTag.toUpperCase() || s.mvId === mv.id) {
            changed = true;
            return { ...s, mvTag: mv.tag };
          }
          return s;
        });
        if (changed) {
          localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(updatedSteps));
        }
      }
    }

    return mv;
  },

  async deleteMV(mvId: string): Promise<boolean> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/mvs?id=${encodeURIComponent(mvId)}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          console.warn('API error deleting MV');
        }
      } catch (err) {
        console.warn('API error deleting MV:', err);
      }
    }

    // Cascade delete in localStorage
    const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
    let deletedMvTag: string | undefined;
    if (rawMvs) {
      const list: ManipulatedVariable[] = JSON.parse(rawMvs);
      const target = list.find((m) => m.id === mvId);
      if (target) deletedMvTag = target.tag;
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(list.filter((m) => m.id !== mvId)));
    }

    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    if (rawSteps) {
      const list: StepTest[] = JSON.parse(rawSteps);
      localStorage.setItem(
        STORAGE_KEYS.STEP_TESTS,
        JSON.stringify(list.filter((s) => s.mvId !== mvId && (!deletedMvTag || s.mvTag?.toUpperCase() !== deletedMvTag.toUpperCase())))
      );
    }

    return true;
  },

  async importMVs(
    campaignId: string,
    importedRows: {
      tag: string;
      description: string;
      controller: any;
      processArea?: string;
      units: string;
      recommendedStepSize: number;
      approvedStepSize: number;
      notes: string;
    }[]
  ): Promise<ManipulatedVariable[]> {
    const raw = localStorage.getItem(STORAGE_KEYS.MVS);
    let allMvs: ManipulatedVariable[] = raw ? JSON.parse(raw) : INITIAL_MVS;
    const now = new Date().toISOString();
    const affectedMvs: ManipulatedVariable[] = [];

    for (const row of importedRows) {
      const existingIdx = allMvs.findIndex(
        (m) => m.campaignId === campaignId && m.tag.toLowerCase() === row.tag.toLowerCase()
      );

      if (existingIdx !== -1) {
        // Update existing MV attributes while preserving move counts
        const existing = allMvs[existingIdx];
        const updated: ManipulatedVariable = {
          ...existing,
          description: row.description || existing.description,
          controller: row.controller || existing.controller,
          processArea: row.processArea || existing.processArea,
          units: row.units || existing.units,
          recommendedStepSize: row.recommendedStepSize ?? existing.recommendedStepSize,
          approvedStepSize: row.approvedStepSize ?? existing.approvedStepSize,
          notes: row.notes || existing.notes,
          updatedAt: now,
        };
        allMvs[existingIdx] = updated;
        affectedMvs.push(updated);
      } else {
        // Create new MV
        const newMv: ManipulatedVariable = {
          id: `mv-${campaignId}-${row.tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now().toString().slice(-4)}`,
          campaignId,
          tag: row.tag.trim(),
          description: row.description.trim() || row.tag.trim(),
          controller: row.controller,
          processArea: row.processArea || '',
          units: row.units || 'units',
          recommendedStepSize: row.recommendedStepSize || 5.0,
          approvedStepSize: row.approvedStepSize || row.recommendedStepSize || 5.0,
          status: 'Not Started',
          positiveMoves: 0,
          negativeMoves: 0,
          validMoves: 0,
          rejectedMoves: 0,
          notes: row.notes || '',
          updatedAt: now,
        };
        allMvs.push(newMv);
        affectedMvs.push(newMv);
      }
    }

    localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));

    if (useRemoteApi) {
      try {
        const res = await fetch('/api/mvs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(affectedMvs),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Database error: status ${res.status}`);
        }
      } catch (err: any) {
        console.error('API error during batch MV import:', err);
        throw err;
      }
    }

    return allMvs.filter((m) => m.campaignId === campaignId);
  },

  // ===== STEP TESTS =====
  async getStepTests(campaignId?: string): Promise<StepTest[]> {
    const targetCampaign = campaignId || this.getCurrentCampaignId();
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/steps?campaignId=${encodeURIComponent(targetCampaign)}`);
        if (res.ok) {
          const remoteSteps: StepTest[] = await res.json();
          const raw = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
          let allSteps: StepTest[] = raw ? JSON.parse(raw) : [];
          allSteps = allSteps.filter((s) => s.campaignId !== targetCampaign).concat(remoteSteps);
          localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(allSteps));
          return remoteSteps;
        }
      } catch (err) {
        console.warn('API error fetching steps:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    const allSteps: StepTest[] = raw ? JSON.parse(raw) : [];
    return allSteps.filter((s) => s.campaignId === targetCampaign);
  },

  async addStepTest(step: StepTest): Promise<StepTest> {
    // 1. Local save
    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    const allSteps: StepTest[] = rawSteps ? JSON.parse(rawSteps) : [];
    const existingIdx = allSteps.findIndex((s) => s.id === step.id);
    if (existingIdx !== -1) {
      allSteps[existingIdx] = step;
    } else {
      allSteps.unshift(step);
    }
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(allSteps));

    // Update corresponding MV status locally
    const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
    const allMvs: ManipulatedVariable[] = rawMvs ? JSON.parse(rawMvs) : INITIAL_MVS;
    const mvIndex = allMvs.findIndex((m) => m.id === step.mvId || m.tag === step.mvTag);

    if (mvIndex !== -1) {
      const targetMv = allMvs[mvIndex];
      if (step.status === 'IN_PROGRESS') {
        targetMv.status = 'Active';
      }
      targetMv.updatedAt = new Date().toISOString();
      allMvs[mvIndex] = targetMv;
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));
    }

    // 2. Remote API sync
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/steps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(step),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error saving step test:', err);
      }
    }

    return step;
  },

  async evaluateStepTest(evaluatedStep: StepTest): Promise<StepTest> {
    // 1. Local update
    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    const allSteps: StepTest[] = rawSteps ? JSON.parse(rawSteps) : [];
    const stepIdx = allSteps.findIndex((s) => s.id === evaluatedStep.id);
    if (stepIdx !== -1) {
      allSteps[stepIdx] = evaluatedStep;
    } else {
      allSteps.unshift(evaluatedStep);
    }
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(allSteps));

    // Update MV tallies and status locally
    const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
    const allMvs: ManipulatedVariable[] = rawMvs ? JSON.parse(rawMvs) : INITIAL_MVS;
    const mvIndex = allMvs.findIndex((m) => m.id === evaluatedStep.mvId || m.tag === evaluatedStep.mvTag);

    if (mvIndex !== -1) {
      const targetMv = allMvs[mvIndex];
      const isPass = evaluatedStep.status === 'PASS';

      if (isPass) {
        targetMv.validMoves += 1;
        if (evaluatedStep.direction === '+') {
          targetMv.positiveMoves += 1;
        } else {
          targetMv.negativeMoves += 1;
        }
      } else {
        targetMv.rejectedMoves += 1;
      }

      const readiness = calculateReadinessScore(targetMv.validMoves);
      if (readiness === 'Ready for Modeling') {
        targetMv.status = 'Model Ready';
      } else {
        targetMv.status = isPass ? 'Step Complete' : 'Rejected';
      }

      targetMv.updatedAt = new Date().toISOString();
      allMvs[mvIndex] = targetMv;
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));
    }

    // 2. Remote API sync
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/steps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(evaluatedStep),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error evaluating step test:', err);
      }
    }

    return evaluatedStep;
  },

  async updateStepTest(step: StepTest): Promise<StepTest> {
    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    const allSteps: StepTest[] = rawSteps ? JSON.parse(rawSteps) : [];
    const stepIdx = allSteps.findIndex((s) => s.id === step.id);
    if (stepIdx !== -1) {
      allSteps[stepIdx] = step;
    } else {
      allSteps.unshift(step);
    }
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(allSteps));

    // Reconcile local MV tallies directly from all remaining/current steps
    const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
    const allMvs: ManipulatedVariable[] = rawMvs ? JSON.parse(rawMvs) : [];
    const mvIdx = allMvs.findIndex((m) => m.id === step.mvId || m.tag === step.mvTag);
    if (mvIdx !== -1) {
      const targetMv = allMvs[mvIdx];
      const remainingSteps = allSteps.filter((s) => s.mvId === targetMv.id || s.mvTag === targetMv.tag);
      const passSteps = remainingSteps.filter((s) => s.status === 'PASS');
      targetMv.validMoves = passSteps.length;
      targetMv.positiveMoves = passSteps.filter((s) => s.direction === '+').length;
      targetMv.negativeMoves = passSteps.filter((s) => s.direction === '-').length;
      targetMv.rejectedMoves = remainingSteps.filter((s) => s.status === 'FAIL').length;
      const hasActive = remainingSteps.some((s) => s.status === 'IN_PROGRESS');

      if (hasActive) {
        targetMv.status = 'Active';
      } else if (targetMv.validMoves >= 20) {
        targetMv.status = 'Model Ready';
      } else if (targetMv.validMoves > 0) {
        targetMv.status = 'Step Complete';
      } else if (targetMv.rejectedMoves > 0) {
        targetMv.status = 'Rejected';
      } else if (targetMv.status !== 'Excluded') {
        targetMv.status = 'Not Started';
      }
      allMvs[mvIdx] = targetMv;
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));
    }

    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/steps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(step),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error updating step test:', err);
      }
    }

    return step;
  },

  async deleteStepTest(stepId: string): Promise<boolean> {
    const rawSteps = localStorage.getItem(STORAGE_KEYS.STEP_TESTS);
    const allSteps: StepTest[] = rawSteps ? JSON.parse(rawSteps) : [];
    const stepToDelete = allSteps.find((s) => s.id === stepId);
    const filtered = allSteps.filter((s) => s.id !== stepId);
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(filtered));

    if (useRemoteApi) {
      try {
        await fetch(`/api/steps?id=${encodeURIComponent(stepId)}`, { method: 'DELETE' });
      } catch (err) {
        console.warn('API error deleting step:', err);
      }
    }

    // Reconcile local MV tallies if step was found
    if (stepToDelete) {
      const rawMvs = localStorage.getItem(STORAGE_KEYS.MVS);
      const allMvs: ManipulatedVariable[] = rawMvs ? JSON.parse(rawMvs) : [];
      const mvIdx = allMvs.findIndex((m) => m.id === stepToDelete.mvId || m.tag === stepToDelete.mvTag);
      if (mvIdx !== -1) {
        const targetMv = allMvs[mvIdx];
        const remainingSteps = filtered.filter((s) => s.mvId === targetMv.id || s.mvTag === targetMv.tag);
        const passSteps = remainingSteps.filter((s) => s.status === 'PASS');
        targetMv.validMoves = passSteps.length;
        targetMv.positiveMoves = passSteps.filter((s) => s.direction === '+').length;
        targetMv.negativeMoves = passSteps.filter((s) => s.direction === '-').length;
        targetMv.rejectedMoves = remainingSteps.filter((s) => s.status === 'FAIL').length;
        const hasActive = remainingSteps.some((s) => s.status === 'IN_PROGRESS');

        if (hasActive) {
          targetMv.status = 'Active';
        } else if (targetMv.validMoves >= 20) {
          targetMv.status = 'Model Ready';
        } else if (targetMv.validMoves > 0) {
          targetMv.status = 'Step Complete';
        } else if (targetMv.rejectedMoves > 0) {
          targetMv.status = 'Rejected';
        } else if (targetMv.status !== 'Excluded') {
          targetMv.status = 'Not Started';
        }
        allMvs[mvIdx] = targetMv;
        localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(allMvs));
      }
    }

    return true;
  },

  // ===== DISTURBANCES =====
  async getDisturbances(campaignId?: string): Promise<Disturbance[]> {
    const targetCampaign = campaignId || this.getCurrentCampaignId();
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/disturbances?campaignId=${encodeURIComponent(targetCampaign)}`);
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error fetching disturbances:', err);
      }
    }
    const raw = localStorage.getItem(STORAGE_KEYS.DISTURBANCES);
    const allDisturbances: Disturbance[] = raw ? JSON.parse(raw) : INITIAL_DISTURBANCES;
    return allDisturbances.filter((d) => d.campaignId === targetCampaign);
  },

  async addDisturbance(disturbance: Disturbance): Promise<Disturbance> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/disturbances`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(disturbance),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error saving disturbance:', err);
      }
    }

    const raw = localStorage.getItem(STORAGE_KEYS.DISTURBANCES);
    const allDist: Disturbance[] = raw ? JSON.parse(raw) : INITIAL_DISTURBANCES;
    const existingIdx = allDist.findIndex((d) => d.id === disturbance.id);
    if (existingIdx !== -1) {
      allDist[existingIdx] = disturbance;
    } else {
      allDist.unshift(disturbance);
    }
    localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(allDist));
    return disturbance;
  },

  async updateDisturbance(disturbance: Disturbance): Promise<Disturbance> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/disturbances`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(disturbance),
        });
        if (res.ok) return await res.json();
      } catch (err) {
        console.warn('API error updating disturbance:', err);
      }
    }

    const raw = localStorage.getItem(STORAGE_KEYS.DISTURBANCES);
    const allDist: Disturbance[] = raw ? JSON.parse(raw) : INITIAL_DISTURBANCES;
    const existingIdx = allDist.findIndex((d) => d.id === disturbance.id);
    if (existingIdx !== -1) {
      allDist[existingIdx] = disturbance;
    } else {
      allDist.unshift(disturbance);
    }
    localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(allDist));
    return disturbance;
  },

  async deleteDisturbance(distId: string): Promise<boolean> {
    if (useRemoteApi) {
      try {
        const res = await fetch(`/api/disturbances?id=${encodeURIComponent(distId)}`, { method: 'DELETE' });
        if (res.ok) return true;
      } catch (err) {
        console.warn('API error deleting disturbance:', err);
      }
    }

    const raw = localStorage.getItem(STORAGE_KEYS.DISTURBANCES);
    const allDist: Disturbance[] = raw ? JSON.parse(raw) : INITIAL_DISTURBANCES;
    const filtered = allDist.filter((d) => d.id !== distId);
    localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(filtered));
    return true;
  },

  // ===== ADMIN / RESET / BACKUP =====
  resetToKfsSeedData(): void {
    localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(INITIAL_CAMPAIGNS));
    localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(INITIAL_MVS));
    localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(INITIAL_STEP_TESTS));
    localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(INITIAL_DISTURBANCES));
    localStorage.setItem(STORAGE_KEYS.CURRENT_CAMPAIGN, 'kfs-apc-2026');
  },

  async exportFullBackup(): Promise<string> {
    let campaigns: Campaign[] = [];
    let mvs: ManipulatedVariable[] = [];
    let stepTests: StepTest[] = [];
    let disturbances: Disturbance[] = [];

    try {
      campaigns = await this.getCampaigns();
      for (const c of campaigns) {
        const [cMvs, cSteps, cDist] = await Promise.all([
          this.getMVs(c.id),
          this.getStepTests(c.id),
          this.getDisturbances(c.id),
        ]);
        mvs.push(...cMvs.map((m) => ({ ...m, campaignId: c.id })));
        stepTests.push(...cSteps.map((s) => ({ ...s, campaignId: c.id })));
        disturbances.push(...cDist.map((d) => ({ ...d, campaignId: c.id })));
      }
    } catch (err) {
      console.warn('API error exporting full backup, falling back to localStorage:', err);
      campaigns = JSON.parse(localStorage.getItem(STORAGE_KEYS.CAMPAIGNS) || '[]');
      mvs = JSON.parse(localStorage.getItem(STORAGE_KEYS.MVS) || '[]');
      stepTests = JSON.parse(localStorage.getItem(STORAGE_KEYS.STEP_TESTS) || '[]');
      disturbances = JSON.parse(localStorage.getItem(STORAGE_KEYS.DISTURBANCES) || '[]');
      if (campaigns.length === 1) {
        const singleId = campaigns[0].id;
        mvs = mvs.map((m) => ({ ...m, campaignId: singleId }));
        stepTests = stepTests.map((s) => ({ ...s, campaignId: singleId }));
        disturbances = disturbances.map((d) => ({ ...d, campaignId: singleId }));
      }
    }

    const backup = {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      projects: campaigns,
      campaigns: campaigns,
      mvs,
      stepTests,
      disturbances,
    };
    return JSON.stringify(backup, null, 2);
  },

  async importFullBackup(jsonContent: string): Promise<{ success: boolean; restoredProjectId?: string; error?: string }> {
    try {
      const data = JSON.parse(jsonContent);

      // 1. Extract campaigns/projects supporting multiple formats
      let rawCampaigns: any[] = [];
      if (Array.isArray(data.projects)) {
        rawCampaigns = data.projects;
      } else if (Array.isArray(data.campaigns)) {
        rawCampaigns = data.campaigns;
      } else if (data.project && typeof data.project === 'object') {
        rawCampaigns = [data.project];
      } else if (data.campaign && typeof data.campaign === 'object') {
        rawCampaigns = [data.campaign];
      } else if (Array.isArray(data) && data.length > 0 && (data[0].id || data[0].name)) {
        rawCampaigns = data;
      }

      // 2. Extract MVs
      let rawMvs: any[] = [];
      if (Array.isArray(data.mvs)) rawMvs = data.mvs;
      else if (Array.isArray(data.variables)) rawMvs = data.variables;
      else if (Array.isArray(data.manipulatedVariables)) rawMvs = data.manipulatedVariables;

      // 3. Extract step tests
      let rawSteps: any[] = [];
      if (Array.isArray(data.stepTests)) rawSteps = data.stepTests;
      else if (Array.isArray(data.steps)) rawSteps = data.steps;

      // 4. Extract disturbances
      let rawDist: any[] = [];
      if (Array.isArray(data.disturbances)) rawDist = data.disturbances;

      if (rawCampaigns.length === 0 && rawMvs.length === 0) {
        return { success: false, error: 'No valid projects or variables found in JSON file.' };
      }

      const campaigns: Campaign[] = rawCampaigns.map((c: any) => ({
        id: String(c.id || `proj-${Date.now()}`),
        name: String(c.name || 'Restored Project'),
        description: String(c.description || ''),
        facility: String(c.facility || 'Keyera Facility'),
        createdAt: c.createdAt || c.created_at || new Date().toISOString(),
        status: (c.status || 'Active') as Campaign['status'],
      }));

      const targetCampaignId = campaigns[0]?.id || (rawMvs[0]?.campaignId ? String(rawMvs[0].campaignId) : `proj-${Date.now()}`);

      if (campaigns.length === 0 && rawMvs.length > 0) {
        campaigns.push({
          id: targetCampaignId,
          name: 'Restored Project',
          description: 'Restored from JSON backup',
          facility: 'Keyera Facility',
          createdAt: new Date().toISOString(),
          status: 'Active',
        });
      }

      // Campaign ID resolver: if single project, all entities belong to it;
      // otherwise, preserve matching IDs or default to primary project ID.
      const validCampaignIds = new Set(campaigns.map((c) => c.id));
      const resolveCampaignId = (origId?: any): string => {
        const str = origId ? String(origId) : '';
        if (campaigns.length === 1) {
          return targetCampaignId;
        }
        if (str && validCampaignIds.has(str)) {
          return str;
        }
        return targetCampaignId;
      };

      // Normalize MVs
      const mvs: ManipulatedVariable[] = rawMvs.map((m: any, idx: number) => {
        const resolvedCampId = resolveCampaignId(m.campaignId || m.campaign_id);
        return {
          id: String(m.id || `mv-${resolvedCampId}-${(m.tag || 'tag').toLowerCase().replace(/[^a-z0-9]/g, '-')}-${idx}`),
          campaignId: resolvedCampId,
          tag: String(m.tag || `TAG-${idx + 1}`),
          description: String(m.description || m.tag || 'Variable'),
          controller: String(m.controller || 'General'),
          processArea: String(m.processArea || m.process_area || ''),
          units: String(m.units || '%'),
          recommendedStepSize: Number(m.recommendedStepSize ?? m.recommended_step_size ?? 5.0),
          approvedStepSize: Number(m.approvedStepSize ?? m.approved_step_size ?? 5.0),
          status: (m.status || 'Not Started') as MVStatus,
          positiveMoves: Number(m.positiveMoves ?? m.positive_moves ?? 0),
          negativeMoves: Number(m.negativeMoves ?? m.negative_moves ?? 0),
          validMoves: Number(m.validMoves ?? m.valid_moves ?? 0),
          rejectedMoves: Number(m.rejectedMoves ?? m.rejected_moves ?? 0),
          notes: String(m.notes || ''),
          updatedAt: m.updatedAt || m.updated_at || new Date().toISOString(),
        };
      });

      // Normalize Step Tests
      const stepTests: StepTest[] = rawSteps.map((s: any, idx: number) => {
        const resolvedCampId = resolveCampaignId(s.campaignId || s.campaign_id);
        return {
          id: String(s.id || `step-${Date.now()}-${idx}`),
          campaignId: resolvedCampId,
          mvId: String(s.mvId || s.mv_id || ''),
          mvTag: String(s.mvTag || s.mv_tag || ''),
          timestamp: String(s.timestamp || new Date().toISOString()),
          engineer: String(s.engineer || 'J. Dhaliwal'),
          operator: String(s.operator || 'Operations'),
          direction: s.direction === '-' ? '-' : '+',
          stepSize: Number(s.stepSize ?? s.step_size ?? 1),
          baselineSp: Number(s.baselineSp ?? s.baseline_sp ?? 0),
          targetSp: Number(s.targetSp ?? s.target_sp ?? 0),
          holdDuration: Number(s.holdDuration ?? s.hold_duration ?? 60),
          completedAt: s.completedAt || s.completed_at || null,
          steadyStateAchieved: s.steadyStateAchieved || s.steady_state_achieved || 'Yes',
          disturbancesPresent: s.disturbancesPresent || s.disturbances_present || 'No',
          analyzerResponseObserved: s.analyzerResponseObserved || s.analyzer_response_observed || 'No',
          dataQualityRating: Number(s.dataQualityRating ?? s.data_quality_rating ?? 5),
          checklist: s.checklist || undefined,
          status: s.status || 'PASS',
          comments: String(s.comments || ''),
          createdAt: s.createdAt || s.created_at || new Date().toISOString(),
        };
      });

      // Normalize Disturbances
      const disturbances: Disturbance[] = rawDist.map((d: any, idx: number) => {
        const resolvedCampId = resolveCampaignId(d.campaignId || d.campaign_id);
        return {
          id: String(d.id || `dist-${Date.now()}-${idx}`),
          campaignId: resolvedCampId,
          timestamp: String(d.timestamp || new Date().toISOString()),
          category: d.category || 'Pump swaps',
          controller: d.controller || 'Plant-Wide',
          description: String(d.description || 'Disturbance'),
          engineer: String(d.engineer || 'J. Dhaliwal'),
          createdAt: d.createdAt || d.created_at || new Date().toISOString(),
        };
      });

      // Sync to Remote API (Cloudflare D1) if active
      if (useRemoteApi) {
        for (const c of campaigns) {
          try {
            await fetch('/api/campaigns', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(c),
            });
          } catch (e) {
            console.warn('API error restoring campaign:', e);
          }
        }

        if (mvs.length > 0) {
          try {
            const resp = await fetch('/api/mvs', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(mvs),
            });
            if (!resp.ok) {
              for (const m of mvs) {
                await fetch('/api/mvs', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(m),
                }).catch(() => {});
              }
            }
          } catch (e) {
            console.warn('API error restoring MV:', e);
          }
        }

        if (stepTests.length > 0) {
          try {
            const resp = await fetch('/api/steps', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(stepTests),
            });
            if (!resp.ok) {
              for (const s of stepTests) {
                await fetch('/api/steps', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(s),
                }).catch(() => {});
              }
            }
          } catch (e) {
            console.warn('API error restoring step test:', e);
          }
        }

        if (disturbances.length > 0) {
          try {
            const resp = await fetch('/api/disturbances', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(disturbances),
            });
            if (!resp.ok) {
              for (const d of disturbances) {
                await fetch('/api/disturbances', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(d),
                }).catch(() => {});
              }
            }
          } catch (e) {
            console.warn('API error restoring disturbance:', e);
          }
        }
      }

      // Always update localStorage
      const existingCampaigns: Campaign[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.CAMPAIGNS) || '[]');
      const mergedCampaigns = [...campaigns];
      for (const ex of existingCampaigns) {
        if (!mergedCampaigns.some((c) => c.id === ex.id)) {
          mergedCampaigns.push(ex);
        }
      }
      localStorage.setItem(STORAGE_KEYS.CAMPAIGNS, JSON.stringify(mergedCampaigns));

      const existingMvs: ManipulatedVariable[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.MVS) || '[]');
      const mergedMvs = [...mvs];
      for (const ex of existingMvs) {
        if (!mergedMvs.some((m) => m.id === ex.id)) {
          mergedMvs.push(ex);
        }
      }
      localStorage.setItem(STORAGE_KEYS.MVS, JSON.stringify(mergedMvs));

      const existingSteps: StepTest[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.STEP_TESTS) || '[]');
      const mergedSteps = [...stepTests];
      for (const ex of existingSteps) {
        if (!mergedSteps.some((s) => s.id === ex.id)) {
          mergedSteps.push(ex);
        }
      }
      localStorage.setItem(STORAGE_KEYS.STEP_TESTS, JSON.stringify(mergedSteps));

      const existingDist: Disturbance[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.DISTURBANCES) || '[]');
      const mergedDist = [...disturbances];
      for (const ex of existingDist) {
        if (!mergedDist.some((d) => d.id === ex.id)) {
          mergedDist.push(ex);
        }
      }
      localStorage.setItem(STORAGE_KEYS.DISTURBANCES, JSON.stringify(mergedDist));

      // Set active project to restored project
      const activeId = campaigns[0]?.id || targetCampaignId;
      if (activeId) {
        this.setCurrentProjectId(activeId);
      }

      return { success: true, restoredProjectId: activeId };
    } catch (err: any) {
      console.error('Failed to import backup:', err);
      return { success: false, error: err.message || 'Invalid JSON format' };
    }
  },
};
